import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCapabilities } from "@/context/CapabilitiesContext";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { Link } from "react-router-dom";
import { Trophy, ChartLine, Buildings, UsersThree } from "@phosphor-icons/react";

const fmtMins = (m) => `${Math.floor((m || 0) / 60)}h ${(m || 0) % 60}m`;
const COLORS = ["#002FA7", "#7C3AED", "#F59E0B", "#22C55E", "#FF3B30", "#0EA5E9"];

export default function Insights() {
  const { user } = useAuth();
  const { can } = useCapabilities();
  const [tab, setTab] = useState("me");
  const [mine, setMine] = useState(null);
  const [org, setOrg] = useState(null);
  const canOrg = can("view_reports");

  useEffect(() => {
    api.get("/reports/my-stats").then(r => setMine(r.data)).catch(() => {});
    if (canOrg) api.get("/reports/organization").then(r => setOrg(r.data)).catch(() => {});
  }, [canOrg]);

  return (
    <div>
      <div className="border-b border-border px-8 lg:px-12 py-8">
        <div className="eyebrow mb-2">Analytics · Insights</div>
        <h1 className="font-display text-4xl font-black tracking-tight flex items-center gap-3">
          <ChartLine size={36} weight="bold" /> Insights
        </h1>
        <p className="text-sm text-muted-foreground mt-2">Personal performance {canOrg && "and organization-wide analytics"}.</p>

        <div className="mt-6 flex gap-0 border border-border w-fit">
          <button onClick={() => setTab("me")} className={`px-6 py-2 text-sm font-medium ${tab === "me" ? "bg-[#0A0A0A] text-white" : "hover:bg-muted"}`} data-testid="tab-me">My Stats</button>
          {canOrg && <button onClick={() => setTab("org")} className={`px-6 py-2 text-sm font-medium border-l border-border ${tab === "org" ? "bg-[#0A0A0A] text-white" : "hover:bg-muted"}`} data-testid="tab-org">Organization</button>}
        </div>
      </div>

      <div className="p-8 lg:p-12">
        {tab === "me" && mine && <MyStats stats={mine} />}
        {tab === "me" && !mine && <div className="text-sm text-muted-foreground">Loading…</div>}
        {tab === "org" && org && <OrgStats stats={org} />}
        {tab === "org" && !org && canOrg && <div className="text-sm text-muted-foreground">Loading…</div>}
      </div>
    </div>
  );
}

function MyStats({ stats }) {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Rank" value={`#${stats.rank.mine || "-"}`} hint={`of ${stats.rank.total_ranked}`} testid="kpi-rank" />
        <Kpi label="Total Score" value={stats.points.total_score} hint={`+${stats.points.from_achievements} from badges`} testid="kpi-score" />
        <Kpi label="Tasks Done" value={stats.tasks.done} hint={`${stats.tasks.pending} pending · ${stats.tasks.overdue} overdue`} testid="kpi-tasks" />
        <Kpi label="Achievements" value={stats.achievements.count} hint="Trophies earned" testid="kpi-ach" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card title="Points">
          <div className="flex items-baseline gap-4">
            <div>
              <div className="eyebrow">Done</div>
              <div className="font-display text-4xl font-black">{stats.points.done}</div>
            </div>
            <div>
              <div className="eyebrow">Pending</div>
              <div className="font-display text-2xl font-black text-muted-foreground">{stats.points.pending}</div>
            </div>
          </div>
        </Card>
        <Card title="Attendance (30d)">
          <div className="flex items-baseline gap-4">
            <div>
              <div className="eyebrow">Days Present</div>
              <div className="font-display text-4xl font-black">{stats.attendance_30d.days_present}</div>
            </div>
            <div>
              <div className="eyebrow">Work</div>
              <div className="text-sm font-mono-mini">{fmtMins(stats.attendance_30d.work_minutes)}</div>
              <div className="eyebrow mt-1">Break</div>
              <div className="text-sm font-mono-mini">{fmtMins(stats.attendance_30d.break_minutes)}</div>
            </div>
          </div>
        </Card>
        <Card title="Leave">
          <div className="flex items-baseline gap-4">
            <div>
              <div className="eyebrow">Balance</div>
              <div className="font-display text-4xl font-black">{stats.leaves.balance}</div>
            </div>
            <div>
              <div className="eyebrow">Approved</div>
              <div className="text-sm font-mono-mini">{stats.leaves.approved}</div>
              <div className="eyebrow mt-1">Pending</div>
              <div className="text-sm font-mono-mini">{stats.leaves.pending}</div>
            </div>
          </div>
        </Card>
      </div>

      {stats.achievements.recent.length > 0 && (
        <div>
          <div className="eyebrow mb-3 flex items-center gap-2"><Trophy size={14} weight="bold" /> Recent achievements</div>
          <div className="flex gap-2 flex-wrap">
            {stats.achievements.recent.map(a => (
              <div key={a.id} className="border border-border p-3 flex items-center gap-2 min-w-[180px]">
                <div className="text-2xl">{a.icon}</div>
                <div>
                  <div className="text-xs font-semibold">{a.label}</div>
                  <div className="text-[10px] font-mono-mini text-muted-foreground">+{a.points} pts</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="text-center pt-4">
        <Link to="/gamification" className="btn-primary inline-flex items-center gap-2">
          <Trophy size={14} weight="bold" /> View full trophy cabinet
        </Link>
      </div>
    </div>
  );
}

function OrgStats({ stats }) {
  const deptData = Object.entries(stats.people.by_department).map(([k, v]) => ({ name: k, value: v }));
  const roleData = Object.entries(stats.people.by_role).map(([k, v]) => ({ name: k, value: v }));
  const projStatusData = Object.entries(stats.projects.by_status).map(([k, v]) => ({ name: k, value: v }));
  const taskStatusData = Object.entries(stats.tasks.by_status).map(([k, v]) => ({ name: k, value: v }));

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="People" value={stats.people.active_users} hint={`${stats.people.total_users} total`} testid="org-kpi-people" />
        <Kpi label="Projects" value={stats.projects.total} hint={`${stats.projects.by_status.active || 0} active`} testid="org-kpi-projects" />
        <Kpi label="Tasks" value={stats.tasks.total} hint={`${stats.tasks.overdue} overdue`} testid="org-kpi-tasks" />
        <Kpi label="Present Today" value={stats.attendance.today_present} testid="org-kpi-attendance" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card title="Attendance (last 7 days)">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stats.attendance.last_7_days}>
              <CartesianGrid stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString("en", { weekday: "short" })} tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
              <Tooltip contentStyle={{ border: '1px solid #0A0A0A', borderRadius: 0 }} />
              <Bar dataKey="present" fill="#002FA7" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Task Status">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={taskStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name}: ${e.value}`}>
                {taskStatusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card title="People by Department">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={deptData} layout="vertical">
              <CartesianGrid stroke="#E5E7EB" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} allowDecimals={false} />
              <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={100} />
              <Tooltip contentStyle={{ border: '1px solid #0A0A0A', borderRadius: 0 }} />
              <Bar dataKey="value" fill="#7C3AED" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="People by Role">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={roleData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name}: ${e.value}`}>
                {roleData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {stats.champions_month?.length > 0 && (
        <div>
          <div className="eyebrow mb-3 flex items-center gap-2"><Trophy size={14} weight="bold" /> This month's champions</div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {stats.champions_month.map((c, i) => (
              <div key={c.user_id} className="border border-border p-4 text-center" data-testid={`org-champ-${i}`}>
                <div className="text-2xl mb-1">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : "🎖"}</div>
                <div className="font-semibold text-sm truncate">{c.user_name}</div>
                <div className="text-xs font-mono-mini text-muted-foreground">{c.points} pts</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {stats.leaves_this_month.total > 0 && (
        <Card title="Leaves this month">
          <div className="flex gap-4 flex-wrap">
            {Object.entries(stats.leaves_this_month.by_status).map(([k, v]) => (
              <div key={k} className="border border-border p-3 min-w-[100px] text-center">
                <div className="eyebrow uppercase">{k}</div>
                <div className="font-display text-2xl font-black">{v}</div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

function Kpi({ label, value, hint, testid }) {
  return (
    <div className="border border-border p-4" data-testid={testid}>
      <div className="eyebrow mb-1">{label}</div>
      <div className="font-display text-3xl font-black">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1 font-mono-mini">{hint}</div>}
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="border border-border p-4">
      <div className="eyebrow mb-3">{title}</div>
      {children}
    </div>
  );
}
